<template>
  <v-dialog v-model="dialog" max-width="600px" persistent scrollable>
    <v-card>
      <v-card-title class="headline">Generate Email</v-card-title>
      <v-card-text>Please select user(s) from below</v-card-text>
      <v-card-text style="height: 350px; overflow-y: auto;">
        <!-- Multi-select dropdown -->
        <v-select
          v-model="selectedSuperUsers"
          :items="users"
          :item-text="user => `${user.firstName} ${user.lastName} ${user.email}`"
          item-value="email"
          label="Select Users"
          multiple
          outlined
          small-chips
          class="mt-5"
          :menu-props="{ maxHeight: '400px' }"
        />
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn
          color="primary"
          :disabled="selectedSuperUsers.length === 0"
          :loading="sending"
          class="px-4"
          @click="sendEmail"
        >
          Generate Email
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {mapGetters, mapState} from 'vuex';
import api from '@/api';
import {apiBaseUrl} from '@/store/config';

export default {
  name: 'EmailModal',
  props: {
    value: Boolean, // v-model binding from parent
    rceAdminUsers: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      dialog: this.value,
      users: [],
      selectedSuperUsers: [],
      senderEmail: this.$OAuth.profile.email,
      senderName: this.$OAuth.profile.givenName + ' ' + this.$OAuth.data.profile.sn,
      loading: false,
      sending: false,
      statusMessage: '',
      statusType: 'success'
    };
  },
  computed: {
  ...mapGetters({
      allSources: 'metadata/sources',
      editorRoles: 'metadata/editorRoles'
    })
  },
  watch: {
    value(val) {
      this.dialog = val;
      if (val) {
      this.fetchAllSuperUsers();
      }
    },
    dialog(val) {
      this.$emit('input', val); // sync with parent
    }
  },
  methods: {
    async fetchAllSuperUsers() {
      this.loading = true;
      try {
        const res = await api.users.getAllSuperUsers(apiBaseUrl);
        console.log('getAllSuperUsers response ',res.result);
        if (res?.result ) {
          this.users = res.result.map(u => ({
          userId: u.userId,
          firstName: u.firstName,
          lastName: u.lastName,
          email: u.email
        }));
        console.log('Load into array users ',this.users);
      } else {
      console.log('getAllSuperUsers returns null value');
      this.users = [];
      this.error = 'Unexpected response format.';
      }
      } catch (err) {
        console.error(err);
        this.statusType = 'error';
        this.statusMessage = 'Failed to load super users.';
      } finally {
        this.loading = false;
      }
    },

    async fetchUsers() {
      const payload = {
        pagination: {
         itemsPerPage: 4000, 
         pageNumber: 1
      }
    }
      await this.$store.dispatch('admin/fetchUsersByGroup', payload);
    },

    async buildEmailBody() {
      const selected = this.selectedSuperUsers;
      await this.fetchUsers();
      const allUsers = this.$store.state.admin.users.items;
      console.log("🧮 Total admin users:",  allUsers.length);

      const selectedList = selected?.length
        ? `<h3>Selected Recipients</h3>
           <ul>${selected
             .map(u => `<li>${u} </li>`)
             .join('')}</ul>`
        : '<p><i>No users selected</i></p>';

      const selectedUserTable = allUsers?.length
        ? `<h3>All Admin Users (Currently Displayed)</h3>
           <table border="1" cellpadding="6" cellspacing="0" style="border-collapse: collapse; width:100%;">
             <thead>
               <tr style="background:#f2f2f2;">
                 <th>User ID</th>
                 <th>Firstname</th>
                 <th>Lastname</th>
                 <th>Content Owner</th>
                 <th>Roles</th>
               </tr>
             </thead>
             <tbody>
               ${allUsers
                 .map(
                   u => {
                if (u.accesses && Array.isArray(u.accesses) && u.accesses.length) {
                 // For the first access entry, include user info
                 const firstRow = `
                   <tr>
                     <td rowspan="${u.accesses.length}">${u.details.userId}</td>
                     <td rowspan="${u.accesses.length}">${u.details.givenName}</td>
                     <td rowspan="${u.accesses.length}">${u.details.familyName}</td>
                     <td>${this.getOwners(u.accesses[0].content)}</td>
                     <td>${this.getRoles(u.accesses[0].roles)}</td>
                   </tr>
                 `;

                 // For remaining access entries, only content + role
                 const otherRows = u.accesses.slice(1).map(a => `
                   <tr>
                     <td>${this.getOwners(a.content)}</td>
                     <td>${this.getRoles(a.roles)}</td>
                   </tr>
                 `).join('');

                 return firstRow + otherRows;
                } else {
                 // fallback if no access
                 return `
                   <tr>
                     <td>${u.details.userId}</td>
                     <td>${u.details.givenName}</td>
                     <td>${u.details.familyName}</td>
                     <td>-</td>
                     <td>-</td>
                   </tr>
                 `;
                }
              })
                 .join('')}
             </tbody>
           </table>`
        : '<p><i>No admin users available</i></p>';

      const emailHtmlBody =  `
        <p>Hello,</p>
        <p>You requested to send an email to the following users:</p>
        ${selectedList}
        <br/>
        ${selectedUserTable}
        <br/>
        <p>Regards,<br/>URH Admin</p>
      `;
      return emailHtmlBody;
    },

    getRoles(roles){
      return roles.map(role => this.editorRoles?.find(r => r.id === role)?.name)?.join(', ')
    },
    getOwners(source) {
      return this.allSources?.find(owner => owner.id === source.id)?.name
    },

    async sendEmail() {
      this.sending = true;
      this.statusMessage = '';

      const emailBody = await this.buildEmailBody();

      //compose email header and body
      const data = {
        to: this.selectedSuperUsers,
        from: this.senderEmail,
        subject: 'URH list of Resource Editor Access Users',
        body: emailBody,
      };
      try {
        await this.$store.dispatch('sendResourceByEmail', data);
        this.statusType = 'success';
        this.statusMessage = 'Emails sent successfully!';
      } catch (e) {
        console.error(e);
        this.statusType = 'error';
        this.statusMessage = 'Failed to send emails. Try again or contact support.';
      } finally {
        this.close();
        this.$store.commit('SET_ALERT', {
          type: this.statusType,
          text: this.statusMessage,
        }, {
          root: true
        });
        this.$store.commit('SET_DISPLAY_ALERT', true, {root: true});
        setTimeout(() => this.$store.commit('SET_DISPLAY_ALERT', false, {root: true}), 20000);
      }
    },
    close() {
      this.sending = false;
      this.dialog = false;
      this.selectedSuperUsers = [];
    }
  }
};
</script>
